import React, { Component } from "react";
import { Grid, Icon, Message, Modal } from 'semantic-ui-react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import styled from 'styled-components';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { CodeDto, CodeGroupDto, UpsertCodeDto } from 'ts-types/api.types';
import { FormState } from 'final-form';
import { deleteCode, findCodeById, searchCode } from 'services/codeServices';
import axios, { CancelTokenSource } from 'axios';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { noop } from 'utils/functionUtils';
import { getAllCodeGroups } from 'services/codeGroupServices';
import UpsertCodeForm from 'components/settings/code/UpsertCodeForm';
import LoaderComponent from 'components/LoaderComponent';
import DeleteActionButton from 'components/DeleteActionButton';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

const NoCodeGroupsPresent = styled.span`
  padding-right: 0.275rem;
`;

interface CodeSearchRequest {
  search: string;
}

interface CodeFieldProps extends FormState {
  values: Partial<CodeSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundCodes: Array<CodeDto>,
  codesLoaded: boolean,
  code?: UpsertCodeDto,
  codeId?: number,
  codeGroups: Array<CodeGroupDto>,
  addEditModalOpen: boolean,
  errorMessage?: String,
  searchValues: string,
  cancelTokenSource: CancelTokenSource,
}

class CodeView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundCodes: [],
      codesLoaded: false,
      code: undefined,
      codeId: undefined,
      codeGroups: [],
      addEditModalOpen: false,
      searchValues: "",
      errorMessage: undefined,
      cancelTokenSource
    };

    getAllCodeGroups(true, cancelTokenSource)
        .then(response => {
          this.setState({
            codeGroups: response
          })
        })
        .catch(noop);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id?: number) => {
    const {cancelTokenSource} = this.state;

    if (id) {
      findCodeById(id, cancelTokenSource)
          .then(response => this.showModal(id, response))
          .catch(noop);
    }
  };

  showModal = (id?: number, code?: CodeDto) => {
    this.setState({
      addEditModalOpen: true,
      code: code,
      codeId: id
    });
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false,
      codeId: undefined,
      code: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        codesLoaded: true
      })
    };

    searchCode(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundCodes: response
          });
        })
        .catch(this.handleError)
        .finally(onFinally);

    this.hideModal();
  };

  deleteCode = (id: number): void => {
    const {foundCodes, cancelTokenSource} = this.state;

    deleteCode(id, cancelTokenSource)
        .then(() => {
          this.setState({
            foundCodes: foundCodes.filter(code => code.id != id)
          })
        })
        .catch(noop);
  };

  handleChange = debounce(({values}: CodeFieldProps): void => {

    if (!this.mounted) {
      return;
    }

    const {search = ""} = values;
    const {cancelTokenSource} = this.state;

    this.setState({
      codesLoaded: false,
      searchValues: search
    });

    const onFinally = () => {
      this.setState({
        codesLoaded: true
      })
    };

    searchCode(search, cancelTokenSource)
        .then(response => {
          this.setState({
            foundCodes: response,
            errorMessage: undefined
          });
        })
        .catch(this.handleError)
        .finally(onFinally);
  }, 300);

  handleError() {

    if (!this || !this.mounted) {
      return;
    }

    const {getMessage} = this.props;

    this.setState({
      errorMessage: getMessage('error.general'),
      codesLoaded: true
    });
  }

  render(): React.ReactNode {

    const {codesLoaded} = this.state;
    const {getMessage} = this.props;

    const codeView = (
        <PraxStyledSegment>

          <div>
            {this.renderSearchCodeSegment()}
          </div>

          <StyledViewTableRow>
            {
              !codesLoaded ?
                  <LoaderComponent message={getMessage("code.loading")}/> :
                  this.renderCodeSegment()
            }
          </StyledViewTableRow>

          {this.renderUpsertCodeModal()}
        </PraxStyledSegment>
    );
    return (
        <React.Fragment>
          {codeView}
        </React.Fragment>
    );
  }

  renderSearchCodeSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderCodeSearchForm}
        />
    );
  };

  renderCodeSearchForm = (): React.ReactNode => {

    const {codeGroups, errorMessage, foundCodes} = this.state;
    const {getMessage} = this.props;

    const numResults = foundCodes.length;

    const disableAddCode = !(codeGroups.filter(codeGroup => !codeGroup.deleted).length > 0);

    return (
        <PxGrid>
          {errorMessage && this.renderErrorSegment()}
          {disableAddCode && this.renderAddCodeWarning()}
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="search"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} disabled={disableAddCode}
                         onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  codeGroupCellRenderer = ({cellData}: any) => {
    const {codeGroups} = this.state;
    const codeGroup = codeGroups && codeGroups.find(cg => cg.id === cellData);
    return (
        <React.Fragment>
          {codeGroup && codeGroup.description}
        </React.Fragment>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("code.confirmDelete")}
              onConfirm={() => this.deleteCode(cellData)}
          />
        </React.Fragment>
    );
  };

  codeRowGetter = ({index}: any) => {
    const {foundCodes} = this.state;

    Object.assign(foundCodes[index], {index: index + 1});

    return foundCodes[index];
  };

  codeRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundCodes} = this.state;

    const codeId = foundCodes[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(codeId)}
        >
          {columns}
        </div>
    );
  };

  renderCodeSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundCodes} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundCodes.length}
            rowGetter={this.codeRowGetter}
            rowRenderer={this.codeRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: (getMessage("code.codeId")),
                dataKey: "codeId"
              },
              {
                width: 300,
                flexGrow: 2,
                label: (getMessage("description")),
                dataKey: "description"
              },

              {
                width: 200,
                flexGrow: 1,
                label: (getMessage("code.group")),
                dataKey: "codeGroupId",
                cellRenderer: (this.codeGroupCellRenderer)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderErrorSegment = (): React.ReactNode => {
    const {errorMessage} = this.state;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message negative>
              <p>{errorMessage}</p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderAddCodeWarning = (): React.ReactNode => {
    const {getMessage} = this.props;

    return (
        <Grid.Row>
          <Grid.Column>
            <Message warning>
              <p>
                <NoCodeGroupsPresent>
                  <Icon name="exclamation triangle" aria-hidden="true"/>
                </NoCodeGroupsPresent>
                {getMessage("code.disableAdd")}
              </p>
            </Message>
          </Grid.Column>
        </Grid.Row>
    );
  };

  renderUpsertCodeModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, code, codeId, codeGroups} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.code")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertCodeForm
                code={code}
                codeId={codeId}
                codeGroups={codeGroups}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }
}

export default withMessages(CodeView);
